import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SetError } from '../actions/toaster';
import { SetUserInfo } from '../actions/user';
import {
  isCustomDurationCartItem,
  isCustomDurationPaymentPlanCartItem,
  isSubscriptionCartItem,
  isTeamSubscriptionCartItem,
} from '../lib/cart';
import { Network } from '@wren/shared';
import { State } from '../store';
import {
  CartItem,
  CartWithItems,
  CustomDurationCartDetails,
  GiftCartDetails,
  FlightCartDetails,
  FundContributionCartDetails,
  OneOffCartDetails,
  SubscriptionCartDetails,
  CustomDurationPaymentPlanDetails,
  TeamSubscriptionCartDetails,
} from '../types';
import { convertCurrency, getCurrencyObjFromCode } from '../util/conversions';
import useTeamMode from './useTeamMode';

// Mirrors the defensive per-request limit on POST /api/carts/add-items.
const MAX_CART_ITEMS_PER_REQUEST = 50;

export default function useCart() {
  const dispatch = useDispatch();
  const {
    cart,
    currency: userCurrency,
    activeTeamId,
  } = useSelector((state: State) => state.user);
  const { isTeamMode } = useTeamMode();

  // Adding a `forTeam` item without also setting `cart.team_id` leaves the cart
  // in a state that `getPaymentModeForCart` rejects as 'invalid' at checkout
  // (it can no longer implicitly fall back to the user's first team). Keep the
  // item-level `forTeam` flag and the cart-level `team_id` in lockstep whenever
  // we add team-attributed items.
  const ensureCartTeamAssociation = useCallback(
    async (forTeam: boolean) => {
      if (!forTeam || !activeTeamId) return;
      if (cart?.teamId === activeTeamId) return;
      const [response, responseBody] = await Network.patch<CartWithItems>(
        'carts/set-for-team',
        { forTeam: true, teamId: activeTeamId }
      );
      if (response.ok && responseBody) {
        dispatch(SetUserInfo({ cart: responseBody }));
      }
    },
    [cart?.teamId, activeTeamId, dispatch]
  );

  const getSubscriptionCartItem = () => {
    return cart?.items.find(isSubscriptionCartItem);
  };

  const getTeamSubscriptionCartItem = () => {
    return cart?.items.find(isTeamSubscriptionCartItem);
  };

  const getCustomDurationCartItem = () => {
    return cart?.items.find(isCustomDurationCartItem);
  };

  const getCustomDurationPaymentPlanCartItem = () => {
    return cart?.items.find(isCustomDurationPaymentPlanCartItem);
  };

  const getFirstPaymentAmount = () => {
    if (!cart) {
      return 0;
    }

    const total = cart.items.reduce((total, item) => {
      if (isSubscriptionCartItem(item) && item.detailsJson.billingCycleAnchor) {
        // subscriptions with a billing cycle anchor are not billed immediately
        return total;
      }
      return total + item.amountInUsdCents;
    }, 0);

    // A payment amount can't be less than zero
    return Math.max(0, Math.floor(total - cart.discount));
  };

  const addCartItem = useCallback(
    async (
      {
        sku,
        amountInUsdCents,
        detailsJson,
      }: Pick<CartItem, 'sku' | 'amountInUsdCents' | 'detailsJson'>,
      options: {
        quantity?: number;
        replaceItems: boolean;
        isTaxDeductible?: boolean;
      } = {
        quantity: 1,
        replaceItems: false,
        isTaxDeductible: false,
      }
    ) => {
      const [response, responseBody] = await Network.post<CartWithItems>(
        `carts/add-item?quantity=${options.quantity ?? 1}&replaceItems=${
          options.replaceItems ?? false
        }&isTaxDeductible=${options.isTaxDeductible ?? false}`,
        {
          sku,
          amountInUsdCents,
          detailsJson,
        }
      );

      if (response.ok && responseBody) {
        dispatch(SetUserInfo({ cart: responseBody }));
        return true;
      } else {
        dispatch(
          SetError(
            'Something went wrong and we could not add this item to your cart.'
          )
        );
        return false;
      }
    },
    [cart?.id]
  );

  const removeCartItem = useCallback(
    async (id: string) => {
      if (!cart) {
        return;
      }

      const [response, responseBody] = await Network.httpDelete<CartWithItems>(
        `carts/${cart.id}/items/${id}`
      );

      if (response.ok && responseBody) {
        dispatch(SetUserInfo({ cart: responseBody }));
      } else {
        dispatch(
          SetError(
            'Something went wrong and we could not remove this item from your cart.'
          )
        );
      }
    },
    [cart?.id]
  );

  const addSubscriptionCartItem = useCallback(
    async (
      {
        amountInUserCurrency,
        ...details
      }: SubscriptionCartDetails & {
        amountInUserCurrency: number;
      },
      options: { replaceItems: boolean } = {
        replaceItems: false,
      }
    ) => {
      const currency = getCurrencyObjFromCode(userCurrency);

      const amountInUsdCents = Math.floor(
        convertCurrency(amountInUserCurrency, currency.code, 'USD')
      );

      return await addCartItem(
        {
          sku: 'subscription',
          amountInUsdCents,
          detailsJson: details,
        },
        options
      );
    },
    [cart?.id, userCurrency]
  );

  const addTeamSubscriptionCartItem = useCallback(
    async (
      details: TeamSubscriptionCartDetails,
      options: { replaceItems: boolean } = {
        replaceItems: false,
      }
    ) => {
      const added = await addCartItem(
        {
          sku: 'team-subscription',
          amountInUsdCents: 0, // team subscriptions are always free to begin with
          detailsJson: details,
        },
        options
      );
      if (added) {
        const [response, responseBody] = await Network.patch<CartWithItems>(
          'carts/set-for-team',
          {
            forTeam: true,
            teamId: details.teamId,
          }
        );
        if (response.ok && responseBody) {
          dispatch(SetUserInfo({ cart: responseBody }));
        } else {
          dispatch(SetError('Could not associate this cart with the team.'));
        }
      }
    },
    [cart?.id, userCurrency]
  );

  const updateSubscriptionCartItem = useCallback(
    async (
      updates: Partial<
        CartItem & { detailsJson: Partial<SubscriptionCartDetails> }
      >
    ) => {
      const existingSubscriptionCartItem = getSubscriptionCartItem();

      if (!existingSubscriptionCartItem) {
        return;
      }

      const {
        detailsJson: { billingCycle },
      } = existingSubscriptionCartItem;

      const newBillingCycle = updates.detailsJson?.billingCycle;
      let amount: number = existingSubscriptionCartItem.amountInUsdCents;

      if (newBillingCycle && newBillingCycle !== billingCycle) {
        if (newBillingCycle === 'month') {
          amount = Math.round(amount / 12);
        } else if (newBillingCycle === 'year') {
          amount *= 12;
        }
      }

      await addCartItem({
        ...existingSubscriptionCartItem,
        ...{
          ...updates,
          amountInUsdCents: amount,
          detailsJson: {
            ...existingSubscriptionCartItem.detailsJson,
            ...updates.detailsJson,
          },
        },
        sku: 'subscription',
      });
    },
    [cart?.id, userCurrency]
  );

  const addCustomDurationCartItem = useCallback(
    async ({
      amountInUsdCents,
      type,
      startDate,
      endDate,
      intent,
      isAddedFromCheckout,
    }: CustomDurationCartDetails & {
      amountInUsdCents: number;
    }) => {
      const details: CustomDurationCartDetails = {
        type,
        startDate,
        endDate,
        intent,
        isAddedFromCheckout,
      };

      await addCartItem({
        sku: 'custom-duration',
        amountInUsdCents,
        detailsJson: details,
      });
    },
    [cart?.id, userCurrency]
  );

  const addCustomDurationPaymentPlanCartItem = useCallback(
    async ({
      amountInUsdCents,
      numberOfPayments,
      portfolioId,
      intent,
    }: CustomDurationPaymentPlanDetails & {
      amountInUsdCents: number;
    }) => {
      const details: CustomDurationPaymentPlanDetails = {
        numberOfPayments,
        portfolioId,
        intent,
      };

      await addCartItem({
        sku: 'custom-duration-payment-plan',
        amountInUsdCents,
        detailsJson: details,
      });
    },
    [cart?.id, userCurrency]
  );

  const addOneOffCartItem = useCallback(
    async ({
      amountInUsdCents,
      forTeam = isTeamMode,
      ...cartDetails
    }: {
      amountInUsdCents: number;
      isTaxDeductible?: boolean;
      replaceItems?: boolean;
    } & OneOffCartDetails) => {
      await addCartItem(
        {
          sku: 'one-off',
          amountInUsdCents,
          detailsJson: {
            ...cartDetails,
            forTeam,
          },
        },
        {
          replaceItems: !!cartDetails.replaceItems,
          isTaxDeductible: cartDetails.isTaxDeductible,
        }
      );
      await ensureCartTeamAssociation(forTeam);
    },
    [cart?.id, userCurrency, isTeamMode, ensureCartTeamAssociation]
  );

  const addFlightCartItem = useCallback(
    async ({
      amountInUsdCents,
      replaceItems = false,
      ...cartDetails
    }: {
      amountInUsdCents: number;
      replaceItems?: boolean;
    } & FlightCartDetails) => {
      await addCartItem(
        {
          sku: 'flight',
          amountInUsdCents,
          detailsJson: {
            ...cartDetails,
            forTeam: isTeamMode,
          },
        },
        { replaceItems }
      );
      await ensureCartTeamAssociation(isTeamMode);
    },
    [cart?.id, userCurrency, isTeamMode, ensureCartTeamAssociation]
  );

  const addGiftCartItem = useCallback(
    async ({
      amountInUsdCents,
      ...cartDetails
    }: {
      amountInUsdCents: number;
      replaceItems?: boolean;
    } & GiftCartDetails) => {
      await addCartItem({
        sku: 'gift',
        amountInUsdCents,
        detailsJson: cartDetails,
      });
    },
    [cart?.id, userCurrency]
  );

  const addCartItems = useCallback(
    async (
      items: Array<{
        sku: string;
        amountInUsdCents: number;
        detailsJson?: unknown;
      }>
    ) => {
      const [response, responseBody] = await Network.post<CartWithItems>(
        'carts/add-items',
        { items }
      );
      if (response.ok && responseBody) {
        dispatch(SetUserInfo({ cart: responseBody }));
        return true;
      } else {
        dispatch(
          SetError(
            'Something went wrong and we could not add items to your cart.'
          )
        );
        return false;
      }
    },
    [cart?.id]
  );

  const addFlightCartItems = useCallback(
    async (items: Array<{ amountInUsdCents: number } & FlightCartDetails>) => {
      const cartItems = items.map(({ amountInUsdCents, ...detailsJson }) => ({
        sku: 'flight',
        amountInUsdCents,
        detailsJson: { ...detailsJson, forTeam: isTeamMode },
      }));
      if (cartItems.length === 0) return false;

      for (
        let index = 0;
        index < cartItems.length;
        index += MAX_CART_ITEMS_PER_REQUEST
      ) {
        const success = await addCartItems(
          cartItems.slice(index, index + MAX_CART_ITEMS_PER_REQUEST)
        );
        if (!success) return false;
      }

      await ensureCartTeamAssociation(isTeamMode);
      return true;
    },
    [addCartItems, isTeamMode, ensureCartTeamAssociation]
  );

  const addFundContributionCartItem = useCallback(
    async ({
      amountInUsdCents,
      ...cartDetails
    }: {
      amountInUsdCents: number;
      replaceItems?: boolean;
      isTaxDeductible?: boolean;
    } & FundContributionCartDetails) => {
      await addCartItem(
        {
          sku: 'fund-contribution',
          amountInUsdCents,
          detailsJson: cartDetails,
        },
        {
          replaceItems: !!cartDetails.replaceItems,
          isTaxDeductible: cartDetails.isTaxDeductible,
        }
      );
    },
    [cart?.id, userCurrency]
  );

  const closeCart = useCallback(async () => {
    if (!cart) {
      return;
    }

    const [response, responseBody] =
      await Network.post<CartWithItems>(`carts/close`);

    if (response.ok && responseBody) {
      dispatch(SetUserInfo({ cart: responseBody }));
    } else {
      dispatch(
        SetError('Something went wrong and we could not close your cart.')
      );
    }
  }, [cart?.id]);

  const openCart = useCallback(async () => {
    if (!cart || cart.completedAt) {
      return;
    }

    const [response, responseBody] =
      await Network.post<CartWithItems>(`carts/open`);

    if (response.ok && responseBody) {
      dispatch(SetUserInfo({ cart: responseBody }));
    } else {
      dispatch(
        SetError('Something went wrong and we could not re-open your cart.')
      );
    }
  }, [cart?.id]);

  const setForTeam = useCallback(
    async (forTeam: boolean, teamId?: string) => {
      const [response, responseBody] = await Network.patch<CartWithItems>(
        'carts/set-for-team',
        { forTeam, teamId }
      );
      if (response.ok && responseBody) {
        dispatch(SetUserInfo({ cart: responseBody }));
        return true;
      }
      return false;
    },
    [dispatch]
  );

  const assignBusiness = useCallback(
    async (input: {
      teamId?: string;
      teamName?: string;
      countryCode?: string;
    }) => {
      const [response, responseBody] = await Network.post<{
        cart?: CartWithItems;
        teamId?: string;
        createdTeam?: boolean;
        message?: string;
      }>('carts/assign-business', input);
      if (!response.ok || !responseBody?.cart || !responseBody.teamId) {
        throw new Error(
          responseBody?.message || 'Could not assign this purchase to a team.'
        );
      }
      dispatch(SetUserInfo({ cart: responseBody.cart }));
      return {
        teamId: responseBody.teamId,
        createdTeam: responseBody.createdTeam === true,
      };
    },
    [dispatch]
  );

  return {
    cart,
    closeCart,
    openCart,
    getSubscriptionCartItem,
    addSubscriptionCartItem,
    addFundContributionCartItem,
    updateSubscriptionCartItem,
    addCustomDurationCartItem,
    addCustomDurationPaymentPlanCartItem,
    getCustomDurationCartItem,
    addOneOffCartItem,
    addCartItem,
    addCartItems,
    removeCartItem,
    getFirstPaymentAmount,
    addGiftCartItem,
    addFlightCartItem,
    addFlightCartItems,
    getCustomDurationPaymentPlanCartItem,
    addTeamSubscriptionCartItem,
    getTeamSubscriptionCartItem,
    setForTeam,
    assignBusiness,
  };
}
